import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { NearshoreService } from '../../../../core/services/nearshor-service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Nearshore } from '../../../../core/models/nearshore';
import { RegexConstants } from '../../../../core/constants/regex-constants';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RequiredMarkDirective } from '../../../../shared/directives/required-mark.directive';

@Component({
  selector: 'em-nearshore-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputTextModule, ButtonModule, ToastModule, TranslatePipe, RequiredMarkDirective],
  templateUrl: './nearshore-form.component.html',
  styleUrl: './nearshore-form.component.scss',
  providers: [MessageService],
})
export class NearshoreFormComponent {
  form!: FormGroup;
  formErrors: any;
  isEditMode: boolean = false;
  errorMessages: any = {};
  translations: any = {};
  originalNearshore!: Nearshore;
  edited: boolean = false;

  constructor(
    private fb: FormBuilder,
    private nearshoreService: NearshoreService,
    private ref: DynamicDialogRef,
    private messageService: MessageService,
    private config: DynamicDialogConfig,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.loadTranslations();
    this.loadErrorMessages();

    this.form = this.fb.group({
      id: [null],
      name: [null, Validators.compose([
        Validators.required,
         Validators.pattern(RegexConstants.NEARSHORE_NAME)])],
      nameEs: [null,
         Validators.compose([
          Validators.required,
           Validators.pattern(RegexConstants.NEARSHORE_NAME_ES)])],
      nameAr: [null,
         Validators.compose([
          Validators.required,
           Validators.pattern(RegexConstants.NEARSHORE_NAME_AR)])],
    });

    if (this.config.data) {
      this.isEditMode = true;
      this.originalNearshore = JSON.parse(JSON.stringify(this.config.data));
      this.populateForm(this.config.data);
    }
    this.translate.onLangChange.subscribe(() => {
      this.loadErrorMessages();
    });
    this.form.valueChanges.subscribe(() => {
      this.edited = true;
    });
  }

   loadErrorMessages(): void {
    this.translate
      .get([
        'layout.configurations.nearShore.NearShoreValidators.required.nameEn',
        'layout.configurations.nearShore.NearShoreValidators.required.nameEs',
        'layout.configurations.nearShore.NearShoreValidators.required.nameAr',
        'layout.configurations.nearShore.NearShoreValidators.pattern.nameEn',
        'layout.configurations.nearShore.NearShoreValidators.pattern.nameEs',
        'layout.configurations.nearShore.NearShoreValidators.pattern.nameAr',
      ])
      .subscribe((translations) => {
        this.errorMessages = {
          required: {
            name: translations['layout.configurations.nearShore.NearShoreValidators.required.nameEn'],
            nameEs: translations['layout.configurations.nearShore.NearShoreValidators.required.nameEs'],
            nameAr: translations['layout.configurations.nearShore.NearShoreValidators.required.nameAr'],
          },
          pattern: {
            name: translations['layout.configurations.nearShore.NearShoreValidators.pattern.nameEn'],
            nameEs: translations['layout.configurations.nearShore.NearShoreValidators.pattern.nameEs'],
            nameAr: translations['layout.configurations.nearShore.NearShoreValidators.pattern.nameAr'],
          },
        };
      });
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.nearShore.editNearShore.errorMessage.title',
        'layout.configurations.nearShore.editNearShore.errorMessage.alreadyExists',
        'layout.configurations.nearShore.addNearShore.errorMessage.title',
        'layout.configurations.nearShore.addNearShore.errorMessage.alreadyExists',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  populateForm(nearshore: Nearshore) {
    this.form.patchValue({
      id: nearshore.id,
      name: nearshore.name,
      nameEs: nearshore.nameEs,
      nameAr: nearshore.nameAr,
    });
  }

  onSave() {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((control) => {
        control.markAsTouched();
      });
      return;
    }

    if (this.isEditMode) {
      this.updateNearshore();
    } else {
      this.addNearshore();
    }
  }

  addNearshore() {
    this.nearshoreService.addNearShore(this.form.value).subscribe({
      next: (nearshore: Nearshore) => {
        this.ref.close(nearshore);
      },
      error: (error) => {
        this.formErrors = error?.error?.fieldErrors;
        console.log('Error while saving the nearshore: ', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.nearShore.addNearShore.errorMessage.title'],
          detail: this.translations['layout.configurations.nearShore.addNearShore.errorMessage.alreadyExists'],
        });
      },
    });
  }

 updateNearshore() {
  if (!this.form.dirty) {
    this.ref.close({ type: 'no-change' });
    return;
  }

  const updatedNearshore = this.form.value;

  this.nearshoreService.updateNearShore(updatedNearshore).subscribe({
    next: (nearshore: Nearshore) => {
      this.ref.close({ type: 'updated', data: nearshore });
    },
    error: (error) => {
      this.formErrors = error?.error?.fieldErrors;

      this.messageService.add({
        severity: 'error',
        summary: this.translations['layout.configurations.nearShore.editNearShore.errorMessage.title'],
        detail: this.translations['layout.configurations.nearShore.editNearShore.errorMessage.alreadyExists'],
      });
    }
  });
}

  isInvalid(key: string) {
    const control = this.form.get(key);

    return {
      invalid: control?.invalid && (control?.dirty || control?.touched),
      message:
        control?.errors &&
        (control.errors['required'] ? this.errorMessages.required[key] 
          : control.errors['minlength']
          ? this.errorMessages.minLength[key]
          : this.errorMessages.pattern[key]),
    };
  }

  getErrorMessage(field: string) {
    return this.formErrors?.find((obj: { field: string }) => obj.field == field)?.errorMessage || '';
  }
}
